import * as React from 'react'
import { cn } from '@/lib/utils'

export interface SegmentedOption<T extends string> {
  value: T
  label: React.ReactNode
}

interface SegmentedControlProps<T extends string> {
  /** Names the group for assistive tech; the options are its answers. */
  label: string
  value: T
  onValueChange: (value: T) => void
  options: readonly SegmentedOption<T>[]
  /** `sm` is 30px tall, beside small buttons and filters; the default is 36px. */
  size?: 'default' | 'sm'
  className?: string
}

/**
 * Plan View's segmented control: two to four short options on a stone track,
 * the chosen one a raised sheet. For switching a view or a mode — "Leads
 * from this period" / "What happened this period", "Show the price" / "Price on
 * request" — where the answer is a few words and changes what is on the screen
 * rather than being the decision itself. A decision that needs a sentence per
 * answer is a `ChoiceCard`.
 *
 * Native radios underneath, visually hidden, so the arrow keys move the choice
 * and a screen reader announces a radio group.
 */
export function SegmentedControl<T extends string>({
  label,
  value,
  onValueChange,
  options,
  size = 'default',
  className,
}: SegmentedControlProps<T>) {
  const name = React.useId()
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn('inline-flex w-fit max-w-full gap-0.5 rounded-md border border-line bg-surface-sunken p-0.5', className)}
    >
      {options.map((option) => {
        const chosen = option.value === value
        return (
          <label
            key={option.value}
            className={cn(
              'inline-flex min-w-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-sm px-2.5 font-medium transition-[background-color,color,box-shadow] duration-100 ease-out has-[:focus-visible]:focus-ds motion-reduce:transition-none',
              size === 'sm' ? 'h-6 text-control' : 'h-7.5 text-sm',
              // The raised sheet, like the current page in the sidebar: white on the stone in
              // light, a step lighter than the track in dark, where white-on-surface would vanish.
              chosen ? 'bg-surface-current text-ink shadow-xs' : 'text-ink-muted hover:text-ink',
            )}
          >
            <input
              type="radio"
              className="sr-only"
              name={name}
              value={option.value}
              checked={chosen}
              onChange={() => onValueChange(option.value)}
            />
            {option.label}
          </label>
        )
      })}
    </div>
  )
}
